import type {
  EquipmentInstancesDto,
  EquipmentSummaryDto,
  SensorSummaryDto,
  PerformanceMetricsDto,
  HardwareConfigDto,
  SensorConfigDto,
  RelayConfigDto,
  InstanceDetailsDto,
  EventCountsDto,
  FlagCountDto,
  GatewayInstanceDto,
} from '@pimm/services/lib/store-equipment';
import { fmtDuration } from './_helper';
import type { DeviceMeta, HubMeta, SensorMeta, SensorRow } from './types';

const eventCount = (e?: EventCountsDto | null): number =>
  (e?.Severity3 ?? 0) + (e?.Severity4 ?? 0);

const flagCount = (f?: FlagCountDto | null): number =>
  (f?.Red ?? 0) + (f?.Black ?? 0);

const coerceNum = (v: unknown): number | null => {
  const n = typeof v === 'string' ? Number(v) : (v as number);
  return Number.isFinite(n) ? n : null;
};

const timespanToMinutes = (ts?: string | null): number => {
  if (!ts) return 0;
  const m = /^(-)?(?:(\d+)\.)?(\d{1,2}):(\d{2}):(\d{2})/.exec(String(ts).trim());
  if (!m) return 0;
  const [, sign, d, h, mi, s] = m;
  const total =
    (Number(d) || 0) * 1440 +
    (Number(h) || 0) * 60 +
    (Number(mi) || 0) +
    Math.round((Number(s) || 0) / 60);
  return sign ? -total : total;
};

const PRIORITY_TO_BATTERY: Record<string, SensorMeta['batteryLevel']> = {
  None: 'ok', L1: 'info', L2: 'warning', L3: 'alarm', L4: 'dead',
};
const batteryFromPriority = (p?: string | null): SensorMeta['batteryLevel'] =>
  PRIORITY_TO_BATTERY[(p ?? 'None').trim()] ?? 'ok';

const additional = (dict?: Record<string, unknown>, key?: string): unknown =>
  dict && key ? dict[key] : undefined;

const groupOf = (eq: EquipmentInstancesDto): string =>
  ((eq.TemperatureMetrics?.Temperature as any)?.ThresholdProfile?.DisplayGroup ?? 'Other').trim() || 'Other';

const isDoorHw = (c?: SensorConfigDto): boolean => /door/i.test(c?.Label ?? '');

const jointiffModel = (deviceType: string | undefined, ext: boolean, linkedTsu: string | null): string | undefined => {
  if (!/JOINTIFF/i.test(deviceType ?? '')) return undefined;
  if (!ext) return 'TSU Internal';
  return linkedTsu ? 'External Probe' : 'RSU External';
};

// Hubs[] (Hardware Config) and Gateways[] (InstancesWithGateways) come from two independent
// backend sources with no shared id — accepted limitation, not fixed here. Base station:
// exactly one per site, match by Kind/Type alone. NEVER match base-station by Label — the
// Gateway's Label for the base station is "Last GSM Time", not "Base Station". Repeater:
// match by Kind/Type AND Label.
const KIND_TO_HUB_TYPE: Record<string, string> = { BaseStation: 'Base Station', Repeater: 'Repeater' };

const matchGateway = (hub: RelayConfigDto, gateways: GatewayInstanceDto[]): GatewayInstanceDto | undefined =>
  gateways.find(g => {
    const hubType = KIND_TO_HUB_TYPE[g.Kind ?? ''];
    if (!hubType || hubType !== (hub.Type ?? '')) return false;
    if (hubType === 'Base Station') return true;
    return g.Label === hub.Label;
  });

export interface AdapterInputs {
  instances: EquipmentInstancesDto[];
  summary: EquipmentSummaryDto[];
  metrics: PerformanceMetricsDto[];
  hardware: HardwareConfigDto;
  gateways?: GatewayInstanceDto[];
  translateName?: (name?: string) => string | undefined;
}

export interface AdapterResult {
  rows: SensorRow[];
  device: DeviceMeta;
}

export const buildSensorRows = ({
  instances,
  summary,
  metrics,
  hardware,
  gateways,
  translateName,
}: AdapterInputs): AdapterResult => {
  const tx = (name?: string) => (translateName ? translateName(name) : name) || name || '';

  const summaryByInstanceId = new Map<string, SensorSummaryDto>();
  for (const eq of summary ?? []) {
    for (const sensor of (eq as any).Sensors ?? []) {
      const iid = sensor.TemperatureInstanceId ?? sensor.DoorInstanceId;
      if (iid) summaryByInstanceId.set(iid, sensor);
    }
  }

  const metricsByEquipmentId = new Map<string, PerformanceMetricsDto>();
  for (const m of metrics ?? []) {
    if (m.EquipmentId && !metricsByEquipmentId.has(m.EquipmentId)) {
      metricsByEquipmentId.set(m.EquipmentId, m);
    }
  }
  const hwByEquipmentId = new Map<string, SensorConfigDto[]>();
  for (const c of hardware?.Sensors ?? []) {
    if (!c.EquipmentId) continue;
    const arr = hwByEquipmentId.get(c.EquipmentId) ?? [];
    arr.push(c);
    hwByEquipmentId.set(c.EquipmentId, arr);
  }

  const rows: SensorRow[] = [];
  const sensors: Record<string, SensorMeta> = {};

  for (const eq of instances ?? []) {
    const eqId = eq.EquipmentId ?? '';
    const group = groupOf(eq);
    const perf = eqId ? metricsByEquipmentId.get(eqId) : undefined;
    const hwList = eqId ? (hwByEquipmentId.get(eqId) ?? []) : [];

    const tempInst = (eq.TemperatureMetrics as any)?.Temperature as InstanceDetailsDto | undefined;
    const doorInst = (eq.DoorMetrics as any)?.Door as InstanceDetailsDto | undefined;

    const tempLabel = tempInst ? tx((tempInst as any).Description ?? eq.EquipmentName) : undefined;
    const doorLabel = doorInst ? tx((doorInst as any).Description ?? `${eq.EquipmentName} Door`) : undefined;

    const pushRow = (
      isDoor: boolean,
      label: string,
      liveInst: InstanceDetailsDto | undefined,
      batteryInst: InstanceDetailsDto | undefined,
      sibling: string | undefined,
    ) => {
      if (!label) return;
      const iid = (liveInst as any)?.InstanceId;
      const sum = iid ? summaryByInstanceId.get(iid) : undefined;
      const hw = isDoor
        ? (hwList.find(isDoorHw) ?? hwList[0])
        : (hwList.find(c => !isDoorHw(c)) ?? hwList[0]);

      const ext = !!additional(hw?.AdditionalProperties as Record<string, unknown>, 'External');
      const linkedTsuRaw = (additional(hw?.AdditionalProperties as Record<string, unknown>, 'LinkedTsu') ?? null) as string | null;
      const linkedTsu = linkedTsuRaw ? tx(linkedTsuRaw) : undefined;
      const resolvedModel =
        (additional(hw?.AdditionalProperties as Record<string, unknown>, 'Model') as string | undefined) ??
        jointiffModel(hardware?.DeviceType, ext, linkedTsuRaw);

      sensors[label] = {
        instanceId: iid,
        equipmentId: eqId || undefined,
        serial: hw?.Serial ?? '',
        imgUrl: (additional(hw?.AdditionalProperties as Record<string, unknown>, 'ImageUrl') ?? '') as string,
        ext,
        enabled: !((liveInst as any)?.OutOfService ?? false),
        isOnline: !!((liveInst as any)?.IsOnline),
        sensorType: isDoor ? 'door' : 'temp',
        linkedTo: sibling,
        linkedTsu,
        batteryLevel: resolvedModel === 'External Probe' ? undefined : batteryFromPriority((batteryInst as any)?.EventPriority),
        lastSeen: (liveInst as any)?.LastUpdateDate,
        model: resolvedModel,
        thresholds: isDoor ? undefined : (tempInst as any)?.Thresholds,
      };

      const offlineMinutes = timespanToMinutes((sum as any)?.OfflineDuration);
      const oosMinutes = timespanToMinutes((sum as any)?.OosDuration);

      rows.push({
        label,
        displayGroup: group,
        uptimePct: (sum as any)?.UptimePct ?? 0,
        uploadPct: (sum as any)?.UploadPct ?? 0,
        offlineDuration: offlineMinutes > 0 ? fmtDuration(offlineMinutes) : null,
        offlineMinutes,
        oosDuration: oosMinutes > 0 ? fmtDuration(oosMinutes) : null,
        offlineEvents: eventCount((sum as any)?.OfflineEvents),
        batteryEvents: eventCount((sum as any)?.BatteryEvents),
        averageTemperature: isDoor ? null : coerceNum(perf?.AverageTemperature),
        inTempPct: isDoor ? null : coerceNum(perf?.InTempPercentage),
        temperatureEvents: isDoor ? null : flagCount(perf?.TemperatureFlagCounts),
        doorEvents: isDoor ? flagCount(perf?.DoorFlagCounts) : 0,
      });
    };

    if (tempInst) pushRow(false, tempLabel!, tempInst, (eq.TemperatureMetrics as any)?.BatteryVoltage, doorLabel);
    if (doorInst) pushRow(true, doorLabel!, doorInst, (eq.DoorMetrics as any)?.BatteryVoltage, tempLabel);
  }

  const hubs: HubMeta[] = (hardware?.Hubs ?? []).map((h: RelayConfigDto, i: number) => {
    const gateway = matchGateway(h, gateways ?? []);
    return {
      label: h.Label ?? h.Type ?? '',
      type: h.Type ?? '',
      serial: h.Serial ?? '',
      imgUrl: (additional(h.AdditionalProperties as Record<string, unknown>, 'ImageUrl') ?? '') as string,
      isPrimary: (additional(h.AdditionalProperties as Record<string, unknown>, 'IsPrimary') ?? i === 0) as boolean,
      // Sensors: IsOnline true=online. Gateways: Offline true=offline — inverted, must negate.
      // Fall back to the old AdditionalProperties.IsOnline only when no Gateway match is
      // found, so an unmatched hub degrades to today's behavior instead of a new regression.
      online: gateway
        ? !gateway.Offline
        : (additional(h.AdditionalProperties as Record<string, unknown>, 'IsOnline') as boolean | undefined),
      model: additional(h.AdditionalProperties as Record<string, unknown>, 'Model') as string | undefined,
      lastReportedTime: gateway?.LastReportedTime,
    };
  });

  return { rows, device: { hubs, sensors } };
};
